import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  MidasPageState,
  SessionInitResponse,
  SidebarState,
  Artifact,
  ArtifactTab,
  Thread,
  ThreadListApiItem,
  ThreadDetailResponse,
  ThreadCountsResponse,
} from "./midasPageModels";
import chatBotApi from "../../shared/chatBot/ChatBotApi";
import {
  filterThreadsForPilot,
  getPilotWireScope,
  shouldDeferPilotThreadFetch,
} from "./pilotThreadScope";
import { ChatMessage, ChatSource, ChatArtifact } from "../../shared/chatBot/models/chatBotModels";
import { normalizeBotImageUrl, mapBotArtifacts } from "../../shared/chatBot/streamBotMessage";

// ============================================================
// Constants
// ============================================================

/**
 * V2 Phase 2A — eviction removed (was: tabs.shift() when length >= MAX_ARTIFACT_TABS).
 * V2 keeps every open artifact tab in state. This constant is now the visible-strip cap
 * for the V2 dock header tab strip — older tabs accumulate in the +N dropdown.
 *
 * V1 production header (`MidasArtifactHeader.tsx`) still iterates `state.artifactTabs`
 * directly without any windowing — without the slice eviction, V1 will render 6+ tab
 * pills with no `+N` chrome. That's accepted per the user decision (V1 is being phased
 * out; minor visual ugliness, no data loss).
 */
export const VISIBLE_TABS_IN_STRIP = 5;
const DEFAULT_ARTIFACT_PANEL_WIDTH = 480;

/** Generate a unique tab ID */
const generateTabId = (): string => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};

// ============================================================
// Async Thunks
// ============================================================

/** Fetch platform-wide session init data from bot API (with mock fallback) */
export const fetchSessionInit = createAsyncThunk(
  "midasPage/fetchSessionInit",
  async () => {
    return chatBotApi.sessionInit();
  }
);

/** Fetch machine-scoped session init data from bot API */
export const fetchMachineSessionInit = createAsyncThunk(
  "midasPage/fetchMachineSessionInit",
  async (machineId: string) => {
    return chatBotApi.sessionInit(machineId);
  }
);

/** Generate a unique message ID */
const generateMsgId = (): string => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};

/** Map bot source objects from snake_case to camelCase */
const mapSources = (sources?: any[]): ChatSource[] | undefined => {
  if (!sources || sources.length === 0) return undefined;
  return sources.map((s: any) => {
    const rawImageUrl = s.image_url ?? s.imageUrl;
    return {
      document: s.document,
      page: s.page,
      pageFrom: s.page_from ?? s.pageFrom,
      pageTo: s.page_to ?? s.pageTo,
      url: s.url,
      // Normalize to relative path so downstream code can safely check startsWith("/")
      imageUrl: rawImageUrl ? normalizeBotImageUrl(rawImageUrl) : undefined,
    };
  });
};

/** Ensure a UTC datetime string ends with "Z" so JS Date parses it as UTC.
 * The .NET backend stores UTC datetimes but may serialize without the "Z" suffix
 * when DateTime.Kind is Unspecified. Without "Z", new Date() treats it as local time,
 * causing wrong relative-time display (e.g. "1h" instead of "~2 min"). */
const ensureUtcSuffix = (iso: string): string => {
  if (!iso) return iso;
  // Already has timezone info (Z, +, or -)
  if (iso.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(iso)) return iso;
  return iso + "Z";
};

/** Map a raw API thread item to our Thread model.
 * The API does not return machine_context / last_message_at, so we use
 * sensible defaults. updated_at is used as last_message_at. Phase 4.4b:
 * `message_count` and `last_message_preview` now arrive on the wire and are
 * propagated as-is. */
const mapApiThread = (item: ThreadListApiItem): Thread => ({
  id: item.id,
  title: item.title,
  machine_context: null,
  machine_id: item.machine_id ?? null,
  message_count: item.message_count ?? 0,
  last_message_preview: item.last_message_preview ?? null,
  last_message_at: ensureUtcSuffix(item.updated_at),
  created_at: ensureUtcSuffix(item.created_at),
});

/** Map a ThreadDetailResponse to a Thread object for the sidebar list. */
const mapDetailToThread = (detail: ThreadDetailResponse): Thread => ({
  id: detail.id,
  title: detail.title,
  machine_context: null,
  machine_id: detail.machine_id ?? null,
  message_count: detail.messages.length,
  // Detail endpoint does not return a server-cleaned preview — fall back to
  // null. The list view (where preview matters) gets the field via the list
  // endpoint mapper above.
  last_message_preview: null,
  last_message_at: ensureUtcSuffix(detail.updated_at),
  created_at: ensureUtcSuffix(detail.created_at),
});

/** Parse a JSON-encoded sources string into a sources array, returning undefined on failure. */
const parseSources = (raw: string | null | undefined): any[] | undefined => {
  if (!raw) return undefined;
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : undefined;
  } catch {
    return undefined;
  }
};

/** Parse a JSON-encoded artifacts string, returning undefined on failure. */
const parseArtifacts = (raw: string | null | undefined): ChatArtifact[] | undefined => {
  if (!raw) return undefined;
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? mapBotArtifacts(arr) : undefined;
  } catch {
    return undefined;
  }
};

/** Map a thread-detail response's API messages (snake_case, `content` field)
 *  to ChatMessage[] (camelCase, `text` field), including `user_image`
 *  artifact extraction for user bubbles.
 *
 *  MODEL-PICKER P3: extracted from `loadThread` (single source of truth) so
 *  the attach-chat column loader (`midasAttachSlice.attachThread`) maps
 *  history IDENTICALLY to the primary chat — do not fork this mapping. */
export const mapThreadDetailMessages = (
  detail: ThreadDetailResponse
): ChatMessage[] =>
  detail.messages.map((m) => {
    const artifacts = parseArtifacts(m.artifacts);
    // MIDAS-BUG-001-P2: extract user_image artifact on user messages and
    // surface it as ChatMessage.imageUrl / imageName so <img> can render
    // the bot-hosted URL on thread reload (blob URLs die on refresh).
    const userImage =
      m.role === "user"
        ? artifacts?.find((a) => a.type === "user_image")
        : undefined;
    const rawText = (m.content ?? "").trim();
    // Treat legacy "[image]" placeholder as empty to avoid rendering literal
    // text. Written by older bot versions before user_image artifact support.
    const text = rawText === "[image]" ? "" : rawText;
    return {
      id: m.id || generateMsgId(),
      role: m.role,
      text,
      timestamp: ensureUtcSuffix(m.created_at),
      sources: mapSources(parseSources(m.sources)),
      artifacts,
      imageUrl: userImage?.url,
      imageName: userImage?.title,
    };
  });

/** Fetch conversation thread list (gracefully returns empty on failure — bot may not support threads yet) */
export const fetchThreads = createAsyncThunk(
  "midasPage/fetchThreads",
  async (args: { limit?: number; offset?: number } | undefined, { getState }) => {
    try {
      // TAJMAC-PILOT: scope at the WIRE. This request is otherwise unfiltered, and its
      // payload feeds MidasChatAreaV3's `.unwrap()` auto-open — fetching the fleet-wide
      // set and filtering on arrival is what produced the auto-open-platform-thread bug.
      // The `condition` below guarantees machineId is non-null whenever we get here as a
      // pilot. Non-pilots pass undefined => identical request to before.
      const { machineId } = getPilotWireScope(getState());
      const raw = await chatBotApi.getThreads(
        args?.limit ?? 50,
        args?.offset ?? 0,
        machineId ?? undefined
      );
      // Map API shape (ThreadListApiItem) to our Thread model
      const threads: Thread[] = (raw.threads || []).map(mapApiThread);
      // Safety net only — a no-op on a wire-scoped response (see pilotThreadScope.ts).
      return { threads: filterThreadsForPilot(threads), total: raw.total ?? 0 };
    } catch {
      // Bot gateway may not support threads yet (returns 404).
      // Return empty list instead of failing — threads are non-critical.
      return { threads: [] as Thread[], total: 0 };
    }
  },
  {
    // TAJMAC-PILOT: a pilot with no machine scope yet is NOT YET ELIGIBLE to fetch.
    // Aborting via `condition` fires neither pending nor fulfilled, so no unscoped
    // request exists to race the scoped one and no empty list is ever stored.
    // NOTE for callers that `.unwrap()` this thunk: an aborted dispatch rejects with a
    // ConditionError — MidasChatAreaV3's rehydrate already runs inside a catch-all, so
    // it degrades to "skip rehydration", which is correct for a pilot (auto-scope, not
    // last-thread inspection, is what restores their context).
    condition: (_arg, { getState }) => !shouldDeferPilotThreadFetch(getState()),
  }
);

/** Fetch thread counts per machine (batch) for sidebar pills */
export const fetchThreadCounts = createAsyncThunk(
  "midasPage/fetchThreadCounts",
  async (machineIds: string[]) => {
    try {
      return await chatBotApi.getThreadCounts(machineIds);
    } catch {
      // Graceful fallback — counts are non-critical
      return { machine_counts: {}, platform_count: 0 } as ThreadCountsResponse;
    }
  }
);

/** Fetch threads for a specific machine (lazy-loaded on accordion expand) */
export const fetchMachineThreads = createAsyncThunk(
  "midasPage/fetchMachineThreads",
  async (machineId: string | null) => {
    try {
      // MIDAS-BUG-002: Raised from 20 to 100 so users with >20 conversations
      // per machine (e.g. 27) can see all of them. Proper paginated "Load more"
      // is tracked as a follow-up improvement.
      const raw = await chatBotApi.getThreads(100, 0, machineId);
      const threads: Thread[] = (raw.threads || []).map(mapApiThread);
      return { machineId, threads };
    } catch {
      return { machineId, threads: [] as Thread[] };
    }
  }
);

/** Create a new conversation thread */
export const createThread = createAsyncThunk(
  "midasPage/createThread",
  async (machineId?: string) => {
    return chatBotApi.createThread(machineId);
  }
);

/** Look up a machine from the customerMachinery Redux store by ID.
 * Returns null if machines aren't loaded or the machine isn't found. */
const findMachineInStore = (state: any, machineId: string) => {
  const machinesMap = state?.customerMachinery?.machines;
  if (!machinesMap || typeof machinesMap !== "object") return null;
  for (const group of Object.values(machinesMap)) {
    if (Array.isArray(group)) {
      const found = (group as any[]).find((m) => m.id === machineId);
      if (found) return found;
    }
  }
  return null;
};

/** Load a thread with its messages, and restore them into chatBotSlice.
 * Uses setMachineContextDirect (synchronous) instead of the async setMachineContext thunk,
 * because the async thunk performs a save/restore message swap that would overwrite
 * the thread messages we just loaded — causing the "welcome page flash" bug.
 *
 * BUG-2026-03-20: Now populates machine display fields (model, serialNumber,
 * manufacturerName, etc.) from Redux customerMachinery state, so that
 * MidasMachineWelcome renders the correct machine name instead of empty string. */
export const loadThread = createAsyncThunk(
  "midasPage/loadThread",
  async (threadId: string, { dispatch, getState }) => {
    // Dynamic import to avoid circular dependency
    const { setMessages, setMachineContextDirect, appendPendingBotMessage } = await import("../../shared/chatBot/chatBotSlice");
    const detail = await chatBotApi.getThread(threadId);
    // Map API messages (snake_case, content field) to ChatMessage format
    // (text field). Shared with midasAttachSlice.attachThread — see
    // mapThreadDetailMessages above.
    const messages: ChatMessage[] = mapThreadDetailMessages(detail);
    // Set machine context FIRST (synchronous — no message swap, no API call).
    // This ensures MidasChatArea renders the correct view immediately.
    if (detail.machine_id) {
      // BUG-2026-03-20 FIX: Look up machine display data from Redux store
      // so MidasMachineWelcome shows the real machine name, serial number, etc.
      const machine = findMachineInStore(getState(), detail.machine_id);
      dispatch(setMachineContextDirect({
        machineId: detail.machine_id,
        model: machine?.model || "",
        serialNumber: machine?.serialNumber || "",
        manufacturerName: machine?.customBrandName || machine?.manufacturer?.name || "",
        internalNumber: machine?.internalNumber || "",
        typeName: machine?.type?.name || "",
        axisConfigName: machine?.axisConfiguration?.name || "",
        controlSystemBrandName: machine?.controlSystemBrand?.name || "",
        productionYear: machine?.productionYear || undefined,
      }));
    } else {
      // Platform thread — clear machine context
      dispatch(setMachineContextDirect(null));
    }
    // Restore messages into chatBot state AFTER context is set.
    // Since setMachineContextDirect is synchronous, messages won't be overwritten.
    dispatch(setMessages(messages));
    // MIDAS-SCOPESTREAM: Check for pending bot message from a background-completing stream.
    // If this thread had a stream that completed while the user was on another thread,
    // the bot message is stored in threadBuffers[threadId].pendingBotMessage.
    const chatState = (getState() as any).chatBot;
    const buffer = chatState?.threadBuffers?.[threadId];
    if (buffer && !buffer.isLoading && buffer.pendingBotMessage) {
      dispatch(appendPendingBotMessage({ threadId }));
    }
    // Map the detail-level thread to our Thread model
    const thread = mapDetailToThread(detail);
    return { thread, messages };
  }
);

/** Rename a conversation thread (optimistic) */
export const renameThread = createAsyncThunk(
  "midasPage/renameThread",
  async ({ id, title }: { id: string; title: string }) => {
    await chatBotApi.renameThread(id, title);
    return { id, title };
  }
);

/** Delete a conversation thread */
export const deleteThread = createAsyncThunk(
  "midasPage/deleteThread",
  async (id: string) => {
    await chatBotApi.deleteThread(id);
    return id;
  }
);

// ============================================================
// Initial State
// ============================================================

const initialState: MidasPageState = {
  sidebarState: "expanded",
  sessionInit: null,
  sessionInitLoading: false,
  sessionInitError: false,
  machineSessionInit: null,
  machineSessionInitLoading: false,
  machineSessionInitError: false,
  artifactPanelOpen: false,
  artifactTabs: [],
  activeArtifactTabId: null,
  artifactPanelWidth: DEFAULT_ARTIFACT_PANEL_WIDTH,
  sidebarStateBeforeArtifact: null,
  // Thread state (M4)
  threads: [],
  activeThreadId: null,
  threadsLoading: false,
  threadDetailLoading: false,
  // Machine-scoped thread state (MIDAS-MSCOPE)
  threadCounts: {},
  platformThreadCount: 0,
  expandedMachines: {},
  threadCountsLoading: false,
  machineThreads: {},
};

// ============================================================
// Slice
// ============================================================

const midasPageSlice = createSlice({
  name: "midasPage",
  initialState,
  reducers: {
    /** Set sidebar state directly */
    setSidebarState: (state, action: PayloadAction<SidebarState>) => {
      state.sidebarState = action.payload;
    },
    /** Toggle sidebar between expanded and collapsed */
    toggleSidebar: (state) => {
      if (state.sidebarState === "expanded") {
        state.sidebarState = "collapsed";
      } else {
        state.sidebarState = "expanded";
      }
    },

    /** Open an artifact in the panel. If max tabs reached, replaces the oldest. */
    openArtifact: (state, action: PayloadAction<Artifact>) => {
      const artifact = action.payload;
      // Check if this exact artifact is already open
      // Images: match by url + page. Rich artifacts: match by type + title.
      const existing = state.artifactTabs.find((t) => {
        if (artifact.type === "table" || artifact.type === "machine_card" || artifact.type === "sr_summary" || artifact.type === "sr_detail" || artifact.type === "diagram_overlay" || artifact.type === "action_form") {
          return t.artifact.type === artifact.type && t.artifact.title === artifact.title;
        }
        return t.artifact.url === artifact.url && t.artifact.page === artifact.page;
      });
      if (existing) {
        // Just activate the existing tab
        state.activeArtifactTabId = existing.id;
        state.artifactPanelOpen = true;
        return;
      }

      const newTab: ArtifactTab = {
        id: generateTabId(),
        artifact,
        zoom: 100,
      };

      // V2 Phase 2A: NO eviction. Every open artifact stays in state.
      // The V2 dock header windows the visible strip via the
      // selectVisibleArtifactTabs / selectOverflowArtifactTabs selectors below;
      // older tabs are accessible via the +N dropdown. V1 header renders all tabs
      // directly (accepted regression — V1 is being phased out).
      state.artifactTabs.push(newTab);
      state.activeArtifactTabId = newTab.id;

      // V2.1-A: Set artifact panel to 50% viewport width on desktop (>1024px)
      const vw = typeof window !== "undefined" ? window.innerWidth : 0;
      if (vw > 1024) {
        state.artifactPanelWidth = Math.floor(vw * 0.5);
      }

      // V2.1-B: Auto-collapse sidebar if chat area would be too narrow (<400px)
      if (!state.artifactPanelOpen && vw > 1024) {
        const sidebarWidth = state.sidebarState === "expanded" ? 280
          : state.sidebarState === "collapsed" ? 56
          : 0;
        const chatRemaining = vw - state.artifactPanelWidth - sidebarWidth;
        if (chatRemaining < 400 && state.sidebarState !== "hidden") {
          state.sidebarStateBeforeArtifact = state.sidebarState;
          state.sidebarState = "collapsed";
        }
      }

      state.artifactPanelOpen = true;
    },

    /**
     * MC-25: Open (or activate) the artifact tab for a `cross_reference` artifact
     * matching the given `page`. If the panel is closed it auto-opens.
     * If no matching artifact exists in the current thread's messages, this is
     * a no-op (defensive — the chip should have been hidden in that case).
     *
     * NOTE: This reducer relies on the matching artifact already being in
     * `artifactTabs` (opened earlier by openArtifact when the SSE stream
     * delivered it) OR by the caller dispatching openArtifact first. In practice
     * the bot emits cross_reference artifacts alongside the <list-ref> tags in
     * the same message, and existing SSE wiring already threads those into
     * richArtifacts → MidasArtifactThumbnailRow → openArtifact on click.
     * For instant-open behaviour without requiring a prior click, the
     * ChatBotMessage handler can also call openArtifact(cross_reference) directly
     * with the artifact it finds in message.artifacts — but for now we match the
     * minimal spec: find by page, activate, auto-open panel.
     */
    openArtifactAtPage: (state, action: PayloadAction<{ page: number }>) => {
      const target = action.payload.page;
      const existing = state.artifactTabs.find((t) => {
        if (t.artifact.source !== "cross_reference") return false;
        const data = (t.artifact.data || {}) as { page?: number };
        const dataPage = typeof data.page === "number" ? data.page : t.artifact.page;
        return dataPage === target;
      });
      if (!existing) return; // No matching artifact — no-op (defensive)
      state.activeArtifactTabId = existing.id;
      state.artifactPanelOpen = true; // Per Q3: auto-open if closed
    },

    /** Close an artifact tab by ID */
    closeArtifactTab: (state, action: PayloadAction<string>) => {
      const tabId = action.payload;
      const idx = state.artifactTabs.findIndex((t) => t.id === tabId);
      if (idx === -1) return;

      state.artifactTabs.splice(idx, 1);

      if (state.artifactTabs.length === 0) {
        // No tabs left — close panel and restore sidebar
        state.artifactPanelOpen = false;
        state.activeArtifactTabId = null;
        if (state.sidebarStateBeforeArtifact != null) {
          state.sidebarState = state.sidebarStateBeforeArtifact;
          state.sidebarStateBeforeArtifact = null;
        }
      } else if (state.activeArtifactTabId === tabId) {
        // Activate the nearest tab
        const newIdx = Math.min(idx, state.artifactTabs.length - 1);
        state.activeArtifactTabId = state.artifactTabs[newIdx].id;
      }
    },

    /** Set the active artifact tab */
    setActiveArtifactTab: (state, action: PayloadAction<string>) => {
      const tab = state.artifactTabs.find((t) => t.id === action.payload);
      if (tab) {
        state.activeArtifactTabId = action.payload;
      }
    },

    /** Close the entire artifact panel and restore sidebar if it was auto-collapsed */
    closeArtifactPanel: (state) => {
      state.artifactPanelOpen = false;
      if (state.sidebarStateBeforeArtifact != null) {
        state.sidebarState = state.sidebarStateBeforeArtifact;
        state.sidebarStateBeforeArtifact = null;
      }
    },

    /** Set artifact panel width (from drag handle) */
    setArtifactPanelWidth: (state, action: PayloadAction<number>) => {
      state.artifactPanelWidth = action.payload;
    },

    /** Set zoom level for the active artifact tab */
    setArtifactZoom: (state, action: PayloadAction<number>) => {
      const tab = state.artifactTabs.find(
        (t) => t.id === state.activeArtifactTabId
      );
      if (tab) {
        tab.zoom = Math.max(50, Math.min(200, action.payload));
      }
    },

    /** Set the active thread ID directly */
    setActiveThreadId: (state, action: PayloadAction<string | null>) => {
      state.activeThreadId = action.payload;
    },

    /** Toggle machine accordion expand/collapse */
    toggleMachineExpand: (state, action: PayloadAction<string>) => {
      const key = action.payload;
      state.expandedMachines[key] = !state.expandedMachines[key];
    },

    /** MIDAS-THREADSYNC Phase 2: Insert an optimistic thread entry on send */
    addOptimisticThread: (
      state,
      action: PayloadAction<{ tempId: string; title: string; machineId: string | null }>
    ) => {
      const { tempId, title, machineId } = action.payload;
      const now = new Date().toISOString();
      const optimistic: Thread = {
        id: tempId,
        title,
        machine_context: null,
        machine_id: machineId,
        message_count: 1,
        last_message_preview: null,
        last_message_at: now,
        created_at: now,
        isOptimistic: true,
      };
      // Insert at top of global thread list
      state.threads.unshift(optimistic);
      state.activeThreadId = tempId;
      // Insert into machineThreads cache
      const mKey = machineId || "platform";
      if (!state.machineThreads[mKey]) {
        state.machineThreads[mKey] = [];
      }
      state.machineThreads[mKey].unshift(optimistic);
      // Increment count pill
      if (mKey === "platform") {
        state.platformThreadCount += 1;
      } else {
        state.threadCounts[mKey] = (state.threadCounts[mKey] || 0) + 1;
      }
    },

    /** MIDAS-THREADSYNC Phase 2: Replace optimistic temp ID with real thread_id from bot */
    reconcileOptimisticThread: (
      state,
      action: PayloadAction<{ tempId: string; realId: string }>
    ) => {
      const { tempId, realId } = action.payload;
      // Update in global threads list
      const globalIdx = state.threads.findIndex((t) => t.id === tempId);
      if (globalIdx >= 0) {
        state.threads[globalIdx].id = realId;
        state.threads[globalIdx].isOptimistic = false;
      }
      // Update active thread ID
      if (state.activeThreadId === tempId) {
        state.activeThreadId = realId;
      }
      // Update in machineThreads cache
      for (const key of Object.keys(state.machineThreads)) {
        const list = state.machineThreads[key];
        const idx = list.findIndex((t) => t.id === tempId);
        if (idx >= 0) {
          list[idx].id = realId;
          list[idx].isOptimistic = false;
          break;
        }
      }
    },

    /** MIDAS-THREADSYNC Phase 2: Remove a failed optimistic thread entry and decrement count */
    removeOptimisticThread: (
      state,
      action: PayloadAction<{ tempId: string; machineId: string | null }>
    ) => {
      const { tempId, machineId } = action.payload;
      state.threads = state.threads.filter((t) => t.id !== tempId);
      if (state.activeThreadId === tempId) {
        state.activeThreadId = state.threads.length > 0 ? state.threads[0].id : null;
      }
      const mKey = machineId || "platform";
      if (state.machineThreads[mKey]) {
        state.machineThreads[mKey] = state.machineThreads[mKey].filter((t) => t.id !== tempId);
      }
      // Decrement count pill
      if (mKey === "platform") {
        state.platformThreadCount = Math.max(0, state.platformThreadCount - 1);
      } else if (state.threadCounts[mKey] != null) {
        state.threadCounts[mKey] = Math.max(0, state.threadCounts[mKey] - 1);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSessionInit.pending, (state) => {
        state.sessionInitLoading = true;
        state.sessionInitError = false;
      })
      .addCase(fetchSessionInit.fulfilled, (state, { payload }) => {
        state.sessionInit = payload;
        state.sessionInitLoading = false;
        state.sessionInitError = false;
      })
      .addCase(fetchSessionInit.rejected, (state) => {
        state.sessionInitLoading = false;
        state.sessionInitError = true;
      })
      // ── Machine session init (Phase B) ──
      .addCase(fetchMachineSessionInit.pending, (state) => {
        state.machineSessionInitLoading = true;
        state.machineSessionInitError = false;
      })
      .addCase(fetchMachineSessionInit.fulfilled, (state, { payload }) => {
        state.machineSessionInit = payload;
        state.machineSessionInitLoading = false;
        state.machineSessionInitError = false;
      })
      .addCase(fetchMachineSessionInit.rejected, (state) => {
        state.machineSessionInitLoading = false;
        state.machineSessionInitError = true;
      })
      // ── Thread thunks (M4) ──
      .addCase(fetchThreads.pending, (state) => {
        state.threadsLoading = true;
      })
      .addCase(fetchThreads.fulfilled, (state, { payload }) => {
        // TAJMAC-PILOT: no-op double-safety — the thunk already filtered the payload at
        // the source (that is what keeps `.unwrap()` consumers honest). Kept so a future
        // edit to the thunk cannot silently reopen the hole here. Identity-preserving for
        // every other role. See pilotThreadScope.ts.
        state.threads = filterThreadsForPilot(payload.threads);
        state.threadsLoading = false;
      })
      .addCase(fetchThreads.rejected, (state) => {
        state.threadsLoading = false;
      })
      .addCase(createThread.fulfilled, (state, { payload }) => {
        const machineId = payload.machine_id ?? null;
        const newThread: Thread = {
          id: payload.id,
          title: payload.title || "New Chat",
          machine_context: null,
          machine_id: machineId,
          message_count: 0,
          last_message_preview: null,
          last_message_at: ensureUtcSuffix(payload.created_at),
          created_at: ensureUtcSuffix(payload.created_at),
        };
        state.threads.unshift(newThread);
        state.activeThreadId = payload.id;
        // Increment count and add to cached machineThreads
        const mKey = machineId || "platform";
        if (mKey === "platform") {
          state.platformThreadCount += 1;
        } else {
          state.threadCounts[mKey] = (state.threadCounts[mKey] || 0) + 1;
        }
        if (state.machineThreads[mKey]) {
          state.machineThreads[mKey].unshift(newThread);
        }
      })
      .addCase(loadThread.pending, (state, action) => {
        state.threadDetailLoading = true;
        // BUG-2026-03-23 FIX: Set activeThreadId IMMEDIATELY on pending, not on fulfilled.
        // Without this, during the async loadThread API call, activeThreadId still points
        // to the OLD thread — so MidasChatArea reads the old thread's streaming buffer,
        // causing streaming tokens to visually appear in the wrong thread and the input
        // to stay disabled (because the old thread's isLoading is still true).
        state.activeThreadId = action.meta.arg;
      })
      .addCase(loadThread.fulfilled, (state, { payload }) => {
        state.threadDetailLoading = false;
        state.activeThreadId = payload.thread.id;
        // Update the thread in the list if it exists
        const idx = state.threads.findIndex(
          (t) => t.id === payload.thread.id
        );
        if (idx >= 0) {
          state.threads[idx] = payload.thread;
        }
      })
      .addCase(loadThread.rejected, (state) => {
        state.threadDetailLoading = false;
      })
      .addCase(renameThread.pending, (state, action) => {
        // Optimistic update
        const { id, title } = action.meta.arg;
        const thread = state.threads.find((t) => t.id === id);
        if (thread) {
          thread.title = title;
        }
      })
      .addCase(deleteThread.fulfilled, (state, { payload: deletedId }) => {
        // Find the thread to decrement the right machine count
        const deletedThread = state.threads.find((t) => t.id === deletedId);
        state.threads = state.threads.filter((t) => t.id !== deletedId);
        if (state.activeThreadId === deletedId) {
          // Switch to the next thread or null
          state.activeThreadId =
            state.threads.length > 0 ? state.threads[0].id : null;
        }
        // Decrement thread count and remove from cached machineThreads
        if (deletedThread) {
          const mKey = deletedThread.machine_id || "platform";
          if (mKey === "platform") {
            state.platformThreadCount = Math.max(0, state.platformThreadCount - 1);
          } else if (state.threadCounts[mKey] != null) {
            state.threadCounts[mKey] = Math.max(0, state.threadCounts[mKey] - 1);
          }
          if (state.machineThreads[mKey]) {
            state.machineThreads[mKey] = state.machineThreads[mKey].filter(
              (t) => t.id !== deletedId
            );
          }
        }
      })
      // ── Thread Counts (MIDAS-MSCOPE) ──
      .addCase(fetchThreadCounts.pending, (state) => {
        state.threadCountsLoading = true;
      })
      .addCase(fetchThreadCounts.fulfilled, (state, { payload }) => {
        state.threadCounts = payload.machine_counts || {};
        state.platformThreadCount = payload.platform_count || 0;
        state.threadCountsLoading = false;
      })
      .addCase(fetchThreadCounts.rejected, (state) => {
        state.threadCountsLoading = false;
      })
      // ── Machine Threads (lazy per accordion) ──
      .addCase(fetchMachineThreads.fulfilled, (state, { payload }) => {
        const key = payload.machineId === null ? "platform" : payload.machineId;
        state.machineThreads[key] = payload.threads;
      });
  },
});

export const {
  setSidebarState,
  toggleSidebar,
  openArtifact,
  openArtifactAtPage,
  closeArtifactTab,
  setActiveArtifactTab,
  closeArtifactPanel,
  setArtifactPanelWidth,
  setArtifactZoom,
  setActiveThreadId,
  toggleMachineExpand,
  addOptimisticThread,
  reconcileOptimisticThread,
  removeOptimisticThread,
} = midasPageSlice.actions;
export default midasPageSlice.reducer;

// ============================================================
// V2 Phase 2A Selectors — strip / dropdown windowing
// ============================================================

/**
 * Visible-in-strip tabs for the V2 dock header (most-recent-first windowing
 * is NOT applied — order matches insertion order to keep tab IDs stable).
 * Returns the FIRST `VISIBLE_TABS_IN_STRIP` tabs; the rest are in the
 * overflow dropdown.
 *
 * Implementation note: the Paper artboard description ("most-recent first")
 * is satisfied by the `+N` dropdown surfacing older tabs. We keep the strip
 * in stable insertion order so a user opening their 6th artifact does not
 * see all 5 existing pills shuffle position. The dropdown labels overflow
 * tabs as "off-strip · accessible via dropdown" per the artboard.
 */
export const selectVisibleArtifactTabs = (state: { midasPage: MidasPageState }) =>
  state.midasPage.artifactTabs.slice(0, VISIBLE_TABS_IN_STRIP);

/** Overflow tabs (those NOT visible in the strip) — populates the +N dropdown. */
export const selectOverflowArtifactTabs = (state: { midasPage: MidasPageState }) =>
  state.midasPage.artifactTabs.slice(VISIBLE_TABS_IN_STRIP);
